import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Modal, Field } from "@/pages/Dashboard";
import { Loader2, Upload, School, ShieldCheck, Sparkles, Wand2 } from "lucide-react";

const ACTION_LABELS = {
  login: "Signed in", register: "Account created", update_school: "Updated school details",
  parent_share_created: "Created a parent link", parent_share_viewed: "Parent opened a link",
  parent_share_signed: "Parent signed a document", reset_password: "Password reset",
};

function AiInstructionsSection({ value, onChange }) {
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);

  const runPreview = async () => {
    setPreviewing(true);
    setPreview(null);
    try {
      const { data } = await api.post("/ai/preview-instructions", { instructions: value });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className="mt-5">
      <p className="text-sm font-semibold text-slate-700 mb-1 font-['Outfit'] flex items-center gap-1.5"><Sparkles size={15} className="text-[#6C8EBF]" /> AI style &amp; instructions</p>
      <p className="text-xs text-slate-400 mb-2">Custom guidance applied to <span className="font-semibold">all</span> AI writing — targets, summaries, support plans, parent notes &amp; meeting agendas. e.g. "Use British/Irish spelling, keep a warm tone, refer to the child by first name, avoid jargon."</p>
      <textarea data-testid="ai-instructions-input" value={value} onChange={(e) => onChange(e.target.value)} rows={4} maxLength={1500}
        placeholder="Add any school-wide preferences for how the AI should write…"
        className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#6C8EBF] resize-y text-sm text-slate-700" />
      <p className="text-[11px] text-slate-400 mt-1 text-right">{value.length}/1500</p>
      <button data-testid="ai-preview-btn" onClick={runPreview} disabled={previewing}
        className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-[#6C8EBF]/40 text-[#4f6f9c] hover:bg-[#6C8EBF]/10 px-4 py-2 text-sm font-semibold font-['Outfit'] transition disabled:opacity-50">
        {previewing ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />} Preview AI writing
      </button>
      {preview && (
        <div data-testid="ai-preview-result" className="mt-3 rounded-xl border border-[#6C8EBF]/25 bg-[#6C8EBF]/[0.06] p-4 space-y-3">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#4f6f9c] mb-1">Sample target</p>
            <p className="text-sm text-slate-700">{preview.target}</p>
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#4f6f9c] mb-1">Sample parent note</p>
            <p className="text-sm text-slate-700">{preview.parent_note}</p>
          </div>
          <p className="text-[11px] text-slate-400">Sample for a fictional pupil — shows how your instructions shape the AI's writing.</p>
        </div>
      )}
    </div>
  );
}

function ActivityLog({ audit }) {
  if (!audit.length) return null;
  return (
    <div className="mt-6 pt-5 border-t border-slate-100">
      <p className="text-sm font-semibold text-slate-700 mb-2 font-['Outfit']">Recent activity</p>
      <div className="space-y-1.5 max-h-44 overflow-y-auto" data-testid="activity-log">
        {audit.slice(0, 12).map((a, i) => (
          <div key={`${a.at}-${a.action}-${i}`} className="flex items-center justify-between text-xs">
            <span className="text-slate-600">{ACTION_LABELS[a.action] || a.action}{a.meta?.student ? ` · ${a.meta.student}` : ""}</span>
            <span className="text-slate-400">{new Date(a.at).toLocaleDateString("en-IE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SchoolSettingsModal({ onClose }) {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.school_name || "");
  const [logo, setLogo] = useState(user?.school_logo || "");
  const [retention, setRetention] = useState(user?.retention_months || 0);
  const [aiInstructions, setAiInstructions] = useState(user?.ai_instructions || "");
  const [audit, setAudit] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/audit").then((r) => setAudit(r.data)).catch(() => setAudit([])); }, []);

  const pickLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > 500 * 1024) { toast.error("Logo must be under 500 KB."); return; }
    const reader = new FileReader();
    reader.onload = () => setLogo(reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/school", { school_name: name, school_logo: logo, retention_months: Number(retention), ai_instructions: aiInstructions });
      setUser(data);
      toast.success("School details saved");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="School details" onClose={onClose}>
      <p className="text-sm text-slate-500 -mt-2 mb-4">
        Used on documents you share with parents (logo &amp; school name appear in the header).
      </p>

      <Field label="School name">
        <input data-testid="school-name-input" value={name} onChange={(e) => setName(e.target.value)}
          placeholder="e.g. St. Francis Xavier Junior School"
          className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#81B29A]" />
      </Field>

      <div className="mt-4">
        <p className="text-sm font-semibold text-slate-700 mb-2 font-['Outfit']">School logo</p>
        <div className="flex items-center gap-4">
          <div className="h-20 w-20 rounded-2xl border border-slate-200 bg-[#F9F9F7] flex items-center justify-center overflow-hidden shrink-0">
            {logo ? <img src={logo} alt="logo preview" className="h-full w-full object-contain" /> : <School size={26} className="text-slate-300" />}
          </div>
          <div className="flex flex-col gap-2">
            <label data-testid="school-logo-upload" className="inline-flex items-center gap-2 cursor-pointer rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-[#81B29A] transition">
              <Upload size={15} /> {logo ? "Change logo" : "Upload logo"}
              <input type="file" accept="image/*" onChange={pickLogo} className="hidden" />
            </label>
            {logo && <button onClick={() => setLogo("")} className="text-xs text-slate-400 hover:text-red-500 text-left">Remove logo</button>}
          </div>
        </div>
      </div>

      <div className="mt-5">
        <p className="text-sm font-semibold text-slate-700 mb-1 font-['Outfit'] flex items-center gap-1.5"><ShieldCheck size={15} className="text-[#5d8a73]" /> Data retention</p>
        <p className="text-xs text-slate-400 mb-2">Automatically delete old parent links &amp; activity logs after this period (runs on app start).</p>
        <select data-testid="retention-select" value={retention} onChange={(e) => setRetention(e.target.value)}
          className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#81B29A]">
          <option value={0}>Keep indefinitely</option>
          <option value={12}>Delete after 12 months</option>
          <option value={24}>Delete after 24 months</option>
          <option value={36}>Delete after 36 months</option>
        </select>
      </div>

      <AiInstructionsSection value={aiInstructions} onChange={setAiInstructions} />

      <button data-testid="save-school-btn" onClick={save} disabled={saving}
        className="mt-6 w-full bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full py-3 font-semibold font-['Outfit'] flex items-center justify-center gap-2 disabled:opacity-60">
        {saving && <Loader2 size={18} className="animate-spin" />} Save details
      </button>

      <ActivityLog audit={audit} />
    </Modal>
  );
}
